import moment from 'moment/moment'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiDatePicker, EuiFieldNumber, EuiFormRow, EuiSpacer, EuiText } from '@elastic/eui'

import { Campaign, CampaignActiveStatus, CampaignStatus, CampaignType } from 'api/interfaces'
import { RootState } from 'app/rootReducer'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

export interface CampaignBudget {
  budget: number
  start?: moment.Moment
  end?: moment.Moment
  method: BudgetMethod
  totalIntervals?: number
}

export interface ICampaignBudgetSetter {
  campaign: Campaign
  onBudgetChange?: (value: CampaignBudget) => void
  campaignBudget: CampaignBudget
}

export enum BudgetMethod {
  Indefinite = 'indefinite',
  Intervals = 'intervals',
  Dates = 'dates'
}

export const InternetMinWeeklyBudget: number = 70
export const InternetMinTotalBudget: number = 100
export const TvMinWeeklyBudget: number = 200
export const TvMinTotalBudget: number = 200
export const BbMinWeeklyBudget: number = 150
export const BbMinTotalBudget: number = 150

export const CampaignBudgetSetter: React.FC<ICampaignBudgetSetter> = props => {
  const whiteLabel = useWhiteLabel()
  const { isShopifyUser, isStaffUser } = useSelector((state: RootState) => state.app)

  const [minWeeklyBudget, setMinWeeklyBudget] = useState(InternetMinWeeklyBudget)
  const [minTotalBudget, setMinTotalBudget] = useState(InternetMinTotalBudget)
  const [minStartDate, setMinStartDate] = useState(moment())

  useEffect(() => {
    if (props.campaign.type === CampaignType.Internet) {
      setMinWeeklyBudget(InternetMinWeeklyBudget)
      setMinTotalBudget(InternetMinTotalBudget)
    } else if (props.campaign.type === CampaignType.TV) {
      setMinWeeklyBudget(TvMinWeeklyBudget)
      setMinTotalBudget(TvMinTotalBudget)
    } else if (props.campaign.type === CampaignType.Billboard) {
      setMinWeeklyBudget(BbMinWeeklyBudget)
      setMinTotalBudget(BbMinTotalBudget)
    } else if (props.campaign.type === CampaignType.Audio) {
      setMinWeeklyBudget(TvMinWeeklyBudget)
      setMinTotalBudget(TvMinTotalBudget)
    }

    if (isStaffUser) {
      setMinStartDate(moment())
    } else if (props.campaign?.activated) {
      setMinStartDate(moment(props.campaign?.activated).add(3, 'day'))
    } else {
      setMinStartDate(moment().add(3, 'day'))
    }
  }, [props.campaign, whiteLabel, isStaffUser])

  const getBudgetLabel = useCallback(() => {
    if (!props.campaign) {
      return ''
    }

    if (isShopifyUser) {
      return `30 Day Budget`
    }

    if (props.campaign?.hasBundle) {
      return 'Campaign Budget'
    }

    return `${props.campaign?.budgetFrequency} Budget`
  }, [props.campaign, isShopifyUser, minWeeklyBudget])

  const onBudgetChange = (value: number) => {
    if (props.onBudgetChange) {
      const newBudget = {
        ...props.campaignBudget,
        budget: value
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onStartChange = (value: moment.Moment) => {
    if (props.onBudgetChange) {
      const momentValue = moment.utc(value.format('MM/DD/yyyy'))
      const newBudget = {
        ...props.campaignBudget,
        start: momentValue
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onEndChange = (value: moment.Moment) => {
    if (props.onBudgetChange) {
      const momentValue = moment.utc(value.format('MM/DD/yyyy'))
      const newBudget = {
        ...props.campaignBudget,
        end: momentValue
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onDurationChange = (value: number) => {
    if (props.onBudgetChange) {
      const newBudget: CampaignBudget = {
        ...props.campaignBudget,
        totalIntervals: value
      }
      props.onBudgetChange(newBudget)
    }
  }

  const ended = props.campaign.status === CampaignStatus.CampaignEnded || props.campaign.status === CampaignStatus.Deleted || props.campaign.status === CampaignStatus.Archived
  const getDurationUnit = (campaign: Campaign) => {
    const frequency = campaign.budgetFrequency
    if (frequency === 'Daily' || frequency === 'Total') {
      return 'Days'
    } else if (frequency === 'Weekly') {
      return 'Weeks'
    } else if (frequency === 'Monthly') {
      return 'Months'
    }
  }

  const getMomentUnit = (campaign: Campaign) => {
    const frequency = campaign.budgetFrequency
    if (frequency === 'Daily' || frequency === 'Total') {
      return 'days'
    } else if (frequency === 'Weekly') {
      return 'weeks'
    } else if (frequency === 'Monthly') {
      return 'months'
    } else {
      return 'weeks'
    }
  }

  return (
    <React.Fragment>
      {props.campaignBudget.method === BudgetMethod.Indefinite ? (
        <EuiFormRow
          label={
            <EuiText size='xs' color={'primary'}>
              <strong>{getBudgetLabel()}</strong>
            </EuiText>
          }>
          <EuiFieldNumber fullWidth prepend={'$'} value={props.campaignBudget.budget} onChange={e => onBudgetChange(Number(e.target.value))} min={minWeeklyBudget} disabled={props.campaign?.hasBundle || !!props.campaign?.activated} />
        </EuiFormRow>
      ) : (
        <React.Fragment>
          <EuiFormRow
            label={
              <EuiText size='xs' color={'primary'}>
                <strong>Start Date</strong>
              </EuiText>
            }>
            <EuiDatePicker fullWidth maxDate={props.campaignBudget.end} disabled={props.campaign.hasBundle || props.campaign.activeStatus === CampaignActiveStatus.Active || ended} selected={props.campaignBudget.start} dateFormat='MM/DD/yyyy' onChange={onStartChange} minDate={minStartDate} showTimeSelect={false} />
          </EuiFormRow>
          {props.campaignBudget.method === BudgetMethod.Dates && (
            <EuiFormRow
              label={
                <EuiText size='xs' color={'primary'}>
                  <strong>End Date</strong>
                </EuiText>
              }>
              <EuiDatePicker utcOffset={0} fullWidth disabled={props.campaign.hasBundle || props.campaign.activeStatus === CampaignActiveStatus.Active || ended} selected={props.campaignBudget.end} dateFormat='MM/DD/yyyy' onChange={onEndChange} minDate={props.campaignBudget.start ?? moment(props.campaign?.activated).add(3, 'day') ?? moment()} showTimeSelect={false} />
            </EuiFormRow>
          )}
          {props.campaignBudget.method === BudgetMethod.Intervals && (
            <EuiFormRow
              helpText={`End date: ${moment(props.campaignBudget.start)
                .add(props.campaignBudget.totalIntervals ?? 1, getMomentUnit(props.campaign))
                .format('MM/DD/yyyy')}`}
              label={
                <EuiText size='xs' color={'primary'}>
                  <strong>Duration</strong>
                </EuiText>
              }>
              <EuiFieldNumber onChange={e => onDurationChange(Number(e.target.value))} disabled={props.campaign.hasBundle || props.campaign.activeStatus === CampaignActiveStatus.Active || ended} min={1} append={getDurationUnit(props.campaign)} value={props.campaignBudget.totalIntervals ?? 1} />
            </EuiFormRow>
          )}
          <EuiFormRow
            label={
              <EuiText size='xs' color={'primary'}>
                <strong>{props.campaign.budgetFrequency} Budget</strong>
              </EuiText>
            }
            helpText={props.campaignBudget.method === BudgetMethod.Dates ? 'Your total budget will be evenly spread across the life of your campaign.' : `Your budget will be spent ${props.campaign.budgetFrequency.toLowerCase()}.`}>
            <EuiFieldNumber step={0.01} fullWidth prepend={'$'} value={props.campaignBudget.budget} onChange={e => onBudgetChange(Number(e.target.value))} min={minTotalBudget} disabled={(!!props.campaign.activated && (props.campaignBudget.start ?? moment()) < moment()) || props.campaign?.hasBundle || ended} />
          </EuiFormRow>
        </React.Fragment>
      )}
      <EuiSpacer />
    </React.Fragment>
  )
}
